/**
 * Merges multiple URL parameter strings into a single query string.
 * @param params - An array of URL parameter strings (e.g. "foo=1&bar=2"). Undefined entries are ignored.
 * @returns A merged query string containing all key-value pairs from the input parameters.
 * @example mergeURLParams(["foo=1", "bar=2&baz=3"]); // "foo=1&bar=2&baz=3"
 */
export const mergeURLParams = (params: Optional<string>[]): string => {
  const sanitizedParams = params.filter(Boolean);
  const merged = new URLSearchParams();
  sanitizedParams.forEach((paramStr) => {
    const searchParams = new URLSearchParams(paramStr ?? '');
    searchParams.forEach((value, key) => merged.set(key, value));
  }, new URLSearchParams());

  return merged.toString();
};

type StringSerializable = string | number | boolean;
type OptionalURLKeyType = StringSerializable | null | undefined;

/**
 * Builds a query string from an object of optional parameters.
 * Any keys with null or undefined values are excluded from the output.
 * @param parameters - An object whose keys are parameter names and values are string, number, or boolean. Null or undefined values are omitted.
 * @returns A URL-encoded query string representing the defined parameters.
 * @example buildOptionalParams({ foo: "1", bar: null, baz: true }); // "foo=1&baz=true"
 */
export const buildOptionalParams = (parameters: { [key: string]: OptionalURLKeyType }): string => {
  const sanitizedParams = Object.fromEntries(
    Object.entries(parameters).filter(([_, value]) => value != null),
  ) as Record<string, string>;

  const params = new URLSearchParams(sanitizedParams);
  return params.toString();
};

/**
 * Constructs a full URL by appending optional query parameters to a base URL.
 * @param baseUrl - The base URL to which query parameters will be appended.
 * @param parameters - An object of optional parameters; null or undefined values are omitted.
 * @returns The base URL with query parameters appended, or the base URL unchanged if no parameters are defined.
 * @example buildUrlWithOptionalParams("https://example.com", { q: "search", page: 2 }); // "https://example.com?q=search&page=2"
 */
export const buildUrlWithOptionalParams = (
  baseUrl: string,
  parameters: { [key: string]: OptionalURLKeyType },
): string => {
  const params = buildOptionalParams(parameters);

  return params ? `${baseUrl}?${params}` : baseUrl;
};
